import CodeComponent from '@/features/CodeComponent/CodeComponent.vue'
import { useAuthStore } from '@/store/auth/auth'
import { computed, defineComponent, reactive, ref, watch, onMounted, onUnmounted } from 'vue'
import { RouterLink, useRouter } from 'vue-router'
import { EMAIL_REG_EXP } from '@/constants/regExp'
import { AUTH_ERROR_MESSAGES_BY_CODE } from '@/constants/errorMessages'
import type { HTTPError, DefaultError } from '@/types/httpError'
import { useMainStore } from '@/store/main/main'

export default defineComponent({
  name: 'AuthForm',
  components: {
    RouterLink,
    CodeComponent,
  },
  props: {
    isUser: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const mainStore = useMainStore()
    const fields = reactive({
      email: '',
      password: '',
    })
    const isPasswordShown = ref<boolean>(false)
    const isCodeStep = ref(false)
    const code = ref('')
    const timer = ref(0)
    const isLoginLoading = ref(false)
    const reqError = ref('')
    const phone = ref<string>('')
    const formRef = ref<HTMLFormElement | null>(null)
    const isAutofilled = ref(false)
    const inputElements = computed(() => formRef.value?.querySelectorAll('input') ?? [])

    const authStore = useAuthStore()
    const router = useRouter()

    const FIELDS_PLACEHOLDERS = {
      email: 'E-mail',
      password: 'Пароль',
    }

    const fieldsTypes = computed(() => ({
      email: 'email',
      password: isPasswordShown.value ? 'text' : 'password',
    }))

    watch(() => isAutofilled.value, (val) => {
      if (val) {
        inputElements.value.forEach(el => {
          el.dispatchEvent(new Event('focus', { bubbles: true }))
        })
      }
    })

    watch(timer, (newTime) => {
      if (newTime > 0) {
        setTimeout(() => timer.value--, 1000)
      }
    })

    const validationErrors = ref<{
      email?: string[]
      password?: string[]
    } | null>(null)

    const isEmailTyped = computed(() => fields.email.length)
    const isEmailAnEmail = computed(() => EMAIL_REG_EXP.test(fields.email))
    const isPasswordValid = computed(() => fields.password.trim().length)

    const validateField = (field: string) => {
      if (validationErrors.value && field in validationErrors.value) {
        delete validationErrors.value[
          field as keyof typeof validationErrors.value
        ]
      }
      if (field === 'email') {
        if (!isEmailAnEmail.value) {
          validationErrors.value = {
            ...validationErrors.value,
            email: ['Проверьте адрес электронной почты'],
          }
        }
        if (!isEmailTyped.value) {
          validationErrors.value = {
            ...validationErrors.value,
            email: ['Поле "E-mail" является обязательным'],
          }
        }
      }
      if (field === 'password' && !isPasswordValid.value) {
        validationErrors.value = {
          ...validationErrors.value,
          password: ['Поле "Пароль" является обязательным'],
        }
      }
    }

    const togglePassword = () => {
      isPasswordShown.value = !isPasswordShown.value
    }

    const resetErrors = () => {
      validationErrors.value = null
      reqError.value = ''
    }
    const isCodeSend = ref(false)

    const sendCodeTimer = () => {
      isCodeSend.value = true
      setTimeout(() => {
        isCodeSend.value = false
      }, 5000)
    }

    const handleLoginSubmit = async () => {
      isLoginLoading.value = true
      resetErrors()
      Object.keys(fields).forEach(field => validateField(field))
      if (validationErrors.value !== null) {
        return
      }
      isPasswordShown.value = false

      try {
        const resPhone = props.isUser ? await authStore.login(fields) : await authStore.adminLogin(fields)
        const phoneFromMessage = resPhone ? resPhone.match(/\d{11}/) : []
        phone.value
          = phoneFromMessage && phoneFromMessage.length > 0
            ? phoneFromMessage[0]
            : ''
        isCodeStep.value = true
        timer.value = 59
        sendCodeTimer()
      } catch (error) {
        const typedErr = error as HTTPError<DefaultError>
        if (typedErr.status === 404) {
          reqError.value = AUTH_ERROR_MESSAGES_BY_CODE[404]
          return
        }
        if (typedErr.status === 422) {
          reqError.value = AUTH_ERROR_MESSAGES_BY_CODE[422]
          validationErrors.value = typedErr.data.data || null
          return
        }
        reqError.value = typedErr.data.error || ''
        validationErrors.value = typedErr.data.data || null
      } finally {
        isLoginLoading.value = false
      }
    }

    const handleSendSMS = async () => {
      isLoginLoading.value = true
      resetErrors()

      const body = {
        phone: phone.value,
      }

      try {
        if (props.isUser) {
          await authStore.sendSMS(body)
        } else {
          await authStore.adminSendSMS(body)
        }
        timer.value = 59
        sendCodeTimer()
      } catch (error) {
        reqError.value = (error as DefaultError).error || ''
      } finally {
        isLoginLoading.value = false
      }
    }

    const handleCompleteSubmit = async () => {
      resetErrors()
      isLoginLoading.value = true

      const body = {
        phone: phone.value,
        code: code.value,
      }

      try {
        if (props.isUser) {
          await authStore.completeLogin(body)
        } else {
          await authStore.adminCompleteLogin(body)
        }

        if (!authStore.isAuth)
          return

        if (props.isUser) {
          if (!authStore.user?.step) {
            await router.push({ name: 'startScreen' })
            return
          }
          if (authStore.user?.step !== 'done') {
            await router.push({ name: 'startScreen', query: { step: authStore.user?.step } })
          } else {
            await router.push({ name: mainStore.isMobile ? 'builder' : 'home' })
          }
        } else {
          await router.push({ name: 'adminCompanies' })
        }
      } catch (error) {
        reqError.value = (error as HTTPError<DefaultError>).data.error || ''
      } finally {
        isLoginLoading.value = false
      }
    }

    const submitHandler = computed(() =>
      isCodeStep.value ? handleCompleteSubmit : handleLoginSubmit,
    )

    const isCompleteSubmitBtnDisabled = computed(() =>
      isCodeStep.value ? code.value.length !== 5 : false,
    )

    const checkIfBrowserChromeBased = () => {
      const ua = navigator.userAgent
      return /Chrome/.test(ua) && !/Edg|OPR/.test(ua)
    }

    const onAutofill = () => isAutofilled.value = true

    const checkAutofill = () => {
      inputElements.value.forEach((input) => {
        input.addEventListener('animationstart', onAutofill)
      })
    }

    onMounted(() => {
      if (checkIfBrowserChromeBased()) {
        checkAutofill()
      }
    })

    onUnmounted(() => {
      inputElements.value.forEach((input) => {
        input.removeEventListener('animationstart', onAutofill)
      })
    })

    return {
      fields,
      isCodeStep,
      code,
      FIELDS_PLACEHOLDERS,
      fieldsTypes,
      timer,
      phone,
      formRef,
      reqError,
      validationErrors,
      isPasswordShown,
      togglePassword,
      submitHandler,
      isCompleteSubmitBtnDisabled,
      handleSendSMS,
      isCodeSend,
    }
  },
})
